import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, Clock, X, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtCountdown = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Viewer-side widget for Private Shows.
 *
 * Lifecycle:
 *   idle (poll config)
 *     ├── click "Request Private" -> price modal -> POST /request -> waiting
 *     └── stream is_in_private (and I'm not the buyer) -> public-locked overlay
 *   waiting (poll status of MY request)
 *     ├── accepted   -> active
 *     ├── declined   -> toast + back to idle
 *     └── expired    -> toast + back to idle
 *   active (poll status; show countdown)
 *     ├── click End  -> POST /end -> idle
 *     ├── countdown 0 -> idle (server auto-expires)
 */
const PrivateShowViewerWidget = ({ streamId, isSelf }) => {
  const [config, setConfig] = useState(null);   // { allow_private, price_per_minute, ... }
  const [myShow, setMyShow] = useState(null);   // pending/accepted/active show I created
  const [showModal, setShowModal] = useState(false);
  const [minutes, setMinutes] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(Date.now());
  const pollRef = useRef(null);
  const statusPollRef = useRef(null);

  // Pull config every 5s.
  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/private-shows/config/${streamId}`, { withCredentials: true });
      setConfig(res.data);
    } catch (e) { /* silent */ }
  }, [streamId]);

  useEffect(() => {
    if (!streamId || isSelf) return;
    fetchConfig();
    pollRef.current = setInterval(fetchConfig, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [streamId, isSelf, fetchConfig]);

  // While we have a pending/active show, poll its status every 3s.
  useEffect(() => {
    if (!myShow) return;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/api/private-shows/status/${myShow.id}`, { withCredentials: true });
        const s = res.data;
        if (s.status === 'declined') {
          toast.error('Creator declined the private show request');
          setMyShow(null);
        } else if (s.status === 'expired' || s.status === 'ended') {
          toast(`Private show ${s.status}`);
          setMyShow(null);
          fetchConfig();
        } else {
          setMyShow(s);
        }
      } catch (e) { /* silent */ }
    };
    tick();
    statusPollRef.current = setInterval(tick, 3000);
    return () => { if (statusPollRef.current) clearInterval(statusPollRef.current); };
  }, [myShow?.id, fetchConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local ticker for smooth countdown.
  useEffect(() => {
    if (!myShow || myShow.status !== 'accepted') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [myShow]);

  // Hydrate minutes once config arrives.
  useEffect(() => {
    if (config && minutes === 0) setMinutes(config.min_minutes || 5);
  }, [config, minutes]);

  const openModal = () => {
    if (!config) return;
    if ((config.your_tokens || 0) < config.min_prepay_tokens) {
      toast.error(`You need at least ${config.min_prepay_tokens} tokens (you have ${config.your_tokens})`);
      return;
    }
    setShowModal(true);
  };

  const submitRequest = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/private-shows/request/${streamId}`,
        { minutes },
        { withCredentials: true },
      );
      setMyShow(res.data);
      setShowModal(false);
      toast.success('Request sent — waiting for creator');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    } finally { setSubmitting(false); }
  };

  const endShow = async () => {
    if (!myShow) return;
    if (!window.confirm('End private show now? No refund will be issued for unused minutes.')) return;
    try {
      await axios.post(`${API}/api/private-shows/end/${myShow.id}`, {}, { withCredentials: true });
      toast.success('Private show ended');
      setMyShow(null);
      fetchConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to end');
    }
  };

  if (!streamId || isSelf || !config) return null;

  // STATE: I'm in an active show (I'm the buyer)
  if (myShow && myShow.status === 'accepted') {
    // Compute live seconds from expires_at for smooth countdown
    const expMs = myShow.expires_at ? Date.parse(myShow.expires_at) : 0;
    const secs = Math.max(0, Math.floor((expMs - now) / 1000));
    return (
      <div className="bg-gradient-to-br from-indigo-900/60 to-blue-900/40 border border-indigo-500/30 rounded-2xl p-5" data-testid="private-show-active-viewer">
        <div className="flex items-center gap-2 mb-3">
          <Lock weight="fill" className="w-5 h-5 text-indigo-300" />
          <h3 className="font-heading text-base font-bold text-white">You&apos;re in Private</h3>
          <span className="ml-auto px-2 py-1 bg-black/30 rounded-lg flex items-center gap-1 text-indigo-200 font-mono text-sm">
            <Clock weight="fill" className="w-3.5 h-3.5" /> {fmtCountdown(secs)}
          </span>
        </div>
        <p className="text-xs text-indigo-200/80 mb-3">
          {myShow.minutes} min × {myShow.price_per_minute} tok = {myShow.prepay_tokens} tokens
        </p>
        <button onClick={endShow}
          className="w-full py-2 bg-red-500/20 text-red-300 rounded-lg text-sm font-semibold hover:bg-red-500/30 transition-colors"
          data-testid="end-private-show-btn">
          End Show (No Refund)
        </button>
      </div>
    );
  }

  // STATE: I have a pending request waiting
  if (myShow && myShow.status === 'pending') {
    return (
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5" data-testid="private-show-waiting-viewer">
        <div className="flex items-center gap-2 mb-2">
          <Lock weight="fill" className="w-5 h-5 text-amber-400 animate-pulse" />
          <h3 className="font-heading text-base font-bold text-white">Request Sent</h3>
        </div>
        <p className="text-xs text-amber-200/80 mb-3">
          Waiting for {config.is_in_private ? 'creator' : 'creator'} to accept… ({myShow.minutes} min, {myShow.prepay_tokens} tok)
        </p>
        <button onClick={() => setMyShow(null)}
          className="w-full py-1.5 bg-slate-800/60 text-slate-300 rounded-lg text-xs hover:bg-slate-700 transition-colors"
          data-testid="cancel-private-request-btn">
          Cancel
        </button>
      </div>
    );
  }

  // STATE: Stream is in a private (I'm a public viewer, NOT the buyer)
  if (config.is_in_private) {
    const exp = config.active_show?.expires_at ? Date.parse(config.active_show.expires_at) : 0;
    const secs = Math.max(0, Math.floor((exp - now) / 1000));
    return (
      <div className="bg-slate-900/80 border border-indigo-500/30 rounded-2xl p-5 text-center" data-testid="private-show-public-locked">
        <Lock weight="fill" className="w-8 h-8 text-indigo-400 mx-auto mb-2" />
        <h3 className="font-heading text-base font-bold text-white mb-1">Creator is in Private</h3>
        <p className="text-xs text-slate-400 mb-3">
          They&apos;ll be back in <span className="text-indigo-300 font-mono">{fmtCountdown(secs)}</span>.
        </p>
        <div className="space-y-2">
          {config.allow_voyeurs && (
            <button disabled
              className="w-full py-2 bg-amber-500/10 text-amber-400/60 rounded-lg text-sm font-semibold cursor-not-allowed border border-amber-500/20"
              title="Voyeur billing ships in the next update"
              data-testid="join-voyeur-btn">
              <Eye weight="fill" className="inline w-4 h-4 mr-1" /> Join as Voyeur ({config.voyeur_price_per_minute} tok/min) — soon
            </button>
          )}
          <a href="/live" className="block w-full py-2 bg-blue-500/20 text-blue-300 rounded-lg text-sm font-semibold hover:bg-blue-500/30 transition-colors">
            Browse other streams <ArrowRight className="inline w-4 h-4 ml-1" />
          </a>
        </div>
      </div>
    );
  }

  // STATE: Idle — can request
  if (!config.allow_private) return null;

  return (
    <>
      <button onClick={openModal}
        className="w-full py-3 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors shadow-lg shadow-indigo-500/20"
        data-testid="request-private-btn">
        <Lock weight="fill" className="w-5 h-5" />
        Request Private — {config.min_prepay_tokens} tok ({config.min_minutes} min)
      </button>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            data-testid="private-request-modal"
            onClick={() => !submitting && setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              className="bg-slate-900 border border-indigo-500/30 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto overscroll-contain"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Lock weight="fill" className="w-6 h-6 text-indigo-400" />
                  <h3 className="font-heading text-xl font-bold text-white">Private Show Request</h3>
                </div>
                <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-white" disabled={submitting}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-slate-400 text-sm mb-5">
                Spend one-on-one time with the creator. You pre-pay up front; minutes
                are locked in. <strong className="text-amber-300">No refund</strong> if
                you end early.
              </p>

              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Length (minutes)</label>
              <input
                type="number"
                min={config.min_minutes}
                max={60}
                value={minutes}
                onChange={(e) => setMinutes(Math.max(config.min_minutes, parseInt(e.target.value) || config.min_minutes))}
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white"
                data-testid="private-minutes-input"
              />
              <p className="text-[10px] text-slate-600 mt-1 mb-5">Minimum {config.min_minutes} min.</p>

              <div className="bg-black/40 rounded-xl p-4 mb-5">
                <div className="flex justify-between text-sm text-slate-400">
                  <span>Rate</span>
                  <span className="text-white font-mono">{config.price_per_minute} tok/min</span>
                </div>
                <div className="flex justify-between text-sm text-slate-400 mt-1">
                  <span>Length</span>
                  <span className="text-white font-mono">{minutes} min</span>
                </div>
                <div className="border-t border-white/5 my-2" />
                <div className="flex justify-between items-center">
                  <span className="text-slate-300 font-semibold">You pay</span>
                  <span className="text-indigo-300 font-bold text-2xl font-mono">{minutes * config.price_per_minute}</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1 text-right">
                  Your balance: {config.your_tokens} tok
                </p>
              </div>

              <button
                onClick={submitRequest}
                disabled={submitting || (minutes * config.price_per_minute) > (config.your_tokens || 0)}
                className="w-full py-3 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white rounded-xl font-bold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="confirm-private-request-btn"
              >
                <Lock weight="fill" className="w-5 h-5" />
                {submitting ? 'Sending…' : `Send Request (${minutes * config.price_per_minute} tok)`}
              </button>
              <p className="text-[10px] text-slate-600 text-center mt-2">
                Tokens are only charged when the creator accepts.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PrivateShowViewerWidget;
